import React, { useState, useEffect } from 'react';
import apiClient from '../config/config';

const DISMISSAL_KEY = 'upcomingSpotlightDismissed';

function UpcomingSpotlight() {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        const res = await apiClient.get('/spotlight-settings/');
        if (cancelled) return;
        const d = res.data || {};

        if (!d.upcoming_enabled || !d.upcoming_brand_name) {
          setBrand(null);
          setLoading(false);
          return;
        }

        // Check dismissal
        try {
          const raw = localStorage.getItem(DISMISSAL_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed.name === d.upcoming_brand_name && Date.now() - parsed.ts < 7 * 24 * 60 * 60 * 1000) {
              setDismissed(true);
              setLoading(false);
              return;
            }
          }
        } catch {}

        // Calculate days until launch
        let daysUntil = null;
        if (d.upcoming_brand_launch_date) {
          const launch = new Date(d.upcoming_brand_launch_date + 'T00:00:00');
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          daysUntil = Math.ceil((launch - today) / (1000 * 60 * 60 * 24));
        }

        setBrand({
          name: d.upcoming_brand_name,
          description: d.upcoming_brand_description || '',
          logo_url: d.upcoming_brand_logo_url || '',
          launch_date: d.upcoming_brand_launch_date || null,
          days_until_launch: daysUntil,
          subtitle: d.upcoming_subtitle || 'Be the first to discover this brand when it launches',
        });
      } catch (err) {
        console.error('Error fetching upcoming spotlight:', err);
        setBrand(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => { cancelled = true; };
  }, []);

  const handleDismiss = () => {
    if (brand?.name) {
      localStorage.setItem(DISMISSAL_KEY, JSON.stringify({ name: brand.name, ts: Date.now() }));
    }
    setDismissed(true);
  };

  const handleGetNotified = () => {
    console.log('Get Notified clicked - triggering popup');
    // Trigger the notify popup
    window.dispatchEvent(new CustomEvent('showNotifyPopup'));
  };

  const cardStyle = {
    position: 'relative',
    marginBottom: '48px',
    background: '#fff',
    borderRadius: '12px',
    padding: '40px',
    border: '1px solid #eee',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
    width: '100%',
  };

  const responsiveStyles = `
    .upcoming-spotlight-wrap { padding: 0 16px; box-sizing: border-box; }
    .upcoming-spotlight-card { box-sizing: border-box; }
    .upcoming-spotlight-inner { display: flex; align-items: center; gap: 40px; flex-wrap: wrap; }
    .upcoming-spotlight-content { flex: 1; min-width: 0; }
    .upcoming-spotlight-image-wrap { flex-shrink: 0; }
    .upcoming-spotlight-image, .upcoming-spotlight-image-placeholder {
      width: 180px; height: 180px; border-radius: 12px;
    }
    @media (max-width: 768px) {
      .upcoming-spotlight-wrap { padding: 0 12px; }
      .upcoming-spotlight-card { padding: 24px 20px; margin-bottom: 32px; }
      .upcoming-spotlight-inner { flex-direction: column; align-items: flex-start; gap: 24px; text-align: left; }
      .upcoming-spotlight-content { min-width: 0; width: 100%; }
      .upcoming-spotlight-image, .upcoming-spotlight-image-placeholder { width: 120px; height: 120px; }
      .upcoming-spotlight-title { font-size: 22px !important; }
      .upcoming-spotlight-dismiss { top: 12px; right: 12px; }
    }
    @media (max-width: 480px) {
      .upcoming-spotlight-wrap { padding: 0 10px; }
      .upcoming-spotlight-card { padding: 20px 16px; margin-bottom: 24px; }
      .upcoming-spotlight-inner { gap: 20px; }
      .upcoming-spotlight-image, .upcoming-spotlight-image-placeholder { width: 100px; height: 100px; }
      .upcoming-spotlight-title { font-size: 20px !important; }
      .upcoming-spotlight-body { font-size: 14px !important; margin-bottom: 20px !important; }
      .upcoming-spotlight-dismiss { top: 10px; right: 10px; width: 28px; height: 28px; }
      .upcoming-spotlight-cta { width: 100%; }
    }
  `;

  if (loading || dismissed || !brand) return null;

  // Build launch text
  let launchText = '';
  if (brand.days_until_launch != null) {
    if (brand.days_until_launch <= 0) launchText = 'Launching today!';
    else if (brand.days_until_launch === 1) launchText = 'Launching tomorrow!';
    else launchText = `Launching in ${brand.days_until_launch} days`;
  }

  return (
    <div className="upcoming-spotlight-wrap">
      <div className="upcoming-spotlight-card" style={cardStyle}>
        <button
          type="button"
          className="upcoming-spotlight-dismiss"
          onClick={handleDismiss}
          style={{
            position: 'absolute', top: '16px', right: '16px',
            background: 'transparent', border: 'none', borderRadius: '50%',
            width: '32px', height: '32px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s',
            zIndex: 10, color: '#888',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#f9f7f5'; e.currentTarget.style.color = '#1a1a1a'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#888'; }}
          aria-label="Dismiss"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <div className="upcoming-spotlight-inner">
          <div className="upcoming-spotlight-image-wrap">
            {brand.logo_url ? (
              <img
                className="upcoming-spotlight-image"
                src={brand.logo_url}
                alt={brand.name}
                style={{ objectFit: 'cover' }}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/default-image.avif'; }}
              />
            ) : (
              <div
                className="upcoming-spotlight-image upcoming-spotlight-image-placeholder"
                style={{
                  background: '#f9f7f5', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#888', fontSize: '32px', fontWeight: '400',
                }}
              >
                {brand.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            )}
          </div>

          <div className="upcoming-spotlight-content">
            <div
              style={{
                display: 'inline-block', padding: '4px 12px', background: '#f9f7f5',
                color: '#1a1a1a', borderRadius: '20px', fontSize: '10px', fontWeight: '600',
                letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '16px',
              }}
            >
              Coming Soon
            </div>
            <h2
              className="upcoming-spotlight-title"
              style={{
                fontSize: '28px', fontWeight: '400', color: '#1a1a1a',
                margin: '0 0 12px 0', lineHeight: '1.3', letterSpacing: '-0.5px',
              }}
            >
              {brand.name}
            </h2>
            <p
              className="upcoming-spotlight-body"
              style={{
                fontSize: '15px', color: '#666', margin: '0 0 28px 0',
                lineHeight: '1.6', fontWeight: '300',
              }}
            >
              {launchText && <>{launchText} </>}
              {brand.description || brand.subtitle}
            </p>
            <button
              type="button"
              onClick={handleGetNotified}
              style={{
                display: 'inline-block', padding: '12px 28px', background: '#1a1a1a',
                color: '#fff', borderRadius: '25px', fontSize: '14px', fontWeight: '400',
                cursor: 'pointer', border: 'none', transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#333'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#1a1a1a'; }}
            >
              Get Notified
            </button>
          </div>
        </div>
      </div>
      <style>{responsiveStyles}</style>
    </div>
  );
}

export default UpcomingSpotlight;
